// MUST: 以下のモジュールをwabunka-srcに移行し、そこからimportするようにする
import Big from 'big.js';

import { buildProviderViewModel, type ProviderViewModel } from '@/models/provider';
import { buildWabunkaChargeType, type WabunkaChargeType } from '@/models/wabunka/wabunkaChargeType';
import { buildWabunkaExperienceOptionListViewModel, type WabunkaExperienceOptionViewModel } from '@/models/wabunka/wabunkaExperienceOption';
import { buildWabunkaInterpreterOptionListViewModel, type WabunkaInterpreterOptionViewModel } from '@/models/wabunka/wabunkaInterpreterOption';
import { buildWabunkaPublishStatus, type WabunkaPublishStatus } from '@/models/wabunka/wabunkaPublishStatus';
import { buildWabunkaReservationQuestionListViewModel, type WabunkaReservationQuestionViewModel } from '@/models/wabunka/wabunkaReservationQuestionAnswer';
import { calcPriceIncludingTax } from '@/utils/priceIncludingTax';
import { resolveReferencePriceDisplay } from '@/wabunka-src/common/features/Experience/models/referencePriceDisplay';
import { formatCurrency } from '@/wabunka-src/common/utils/currency';
import { DEFAULT_TAX_RATE } from '@/wabunka-src/constants/enExperiences';
import { SERVICE_FEE_RATE } from '@/wabunka-src/constants/wabunkaPriceCalculation';
import { buildWabunkaCancellationPolicyTrigger, type WabunkaCancellationPolicyTrigger } from '@/wabunka-src/features/Experience/models/cancellationPolicyTrigger';
import { buildWabunkaExperienceTagListViewModel, type WabunkaExperienceTagListViewModel } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTag';

import type { WabunkaCurrencyConvertedPrice } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_currency';
import type { DurationObject, WabunkaExperience, WabunkaExperienceImage, WabunkaExperienceListResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';

/** 体験画像 */
export interface ExperienceImageViewModel {
  /** ID */
  id: string;
  /** オプションID */
  experienceId: string;
  /** 画像のURL */
  imageUrl: string;
  /** シーケンスID */
  sequenceId: number;
}

export type ExperienceImageListViewModel = ExperienceImageViewModel[];
/**
 * Wabunka 体験ビューモデル
 */
export interface ExperienceViewModel {
  /** ID */
  id: string;
  /** 体験名 */
  name: string;
  /** [Optional] 事業者 */
  provider?: ProviderViewModel;
  /** 課金タイプ */
  chargeType: WabunkaChargeType;
  /** 受入可能人数 */
  maxGuests: number;
  /** 最少予約人数 */
  minGuestsPerReservation: number;
  /** スラッグ */
  slug: string;
  /** 一人あたりの金額 */
  price: number;
  /** 参考料金 */
  referencePrice: number | undefined;
  /** 参考料金の注釈文（リッチテキスト） */
  referencePriceAnnotation?: string;
  /** 価格が公開か非公開か */
  isPublicPrice: boolean;
  /** 所要時間 */
  experienceDuration: DurationObject | undefined;
  /** キャンセルポリシーのトリガー */
  cancellationPolicyTrigger: WabunkaCancellationPolicyTrigger;
  /** キャンセルポリシーのラダー設定が有効かどうか */
  isLadderEnabled: boolean;
  /** キャンセル・変更締切（N時間前） */
  confirmedBookingLeadHours: number;
  /** 返金ありのゲストキャンセル期限（N日前） */
  refundableCancellationDaysForGuest: number;
  /** 体験オプション */
  experienceOptions: WabunkaExperienceOptionViewModel[];
  /** 通訳オプション */
  interpreterOptions: WabunkaInterpreterOptionViewModel[];
  /** 予約質問 */
  reservationQuestions: WabunkaReservationQuestionViewModel[];
  /**
   * 追加プロパティ
   */
  priceIncludingTax: number;
  /**
   * 体験画像(1枚目をサムネイルとして使用している)
   */
  images: ExperienceImageListViewModel;
  /**
   * タグ一覧
   */
  tags: WabunkaExperienceTagListViewModel;
  /** 公開状態 */
  publishStatus: WabunkaPublishStatus;
  /** 開催場所概要 */
  venueOverview: string;
  /** 開催場所詳細 */
  venueDetail: string;
  /** 通貨換算済み価格（JPY以外の通貨が指定された場合に設定される） */
  currencyConvertedPrice?: WabunkaCurrencyConvertedPrice;
  /** 通訳手配要否（Wabunkaで通訳手配が必要か。事業者が英語対応可能な場合は不要） */
  isInterpreterNeeded: boolean;
}

/**
 * 体験詳細用ビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaExperienceViewModel} - 体験詳細用ビューモデルのデフォルト値
 */
export const createDefaultExperienceViewModel = (): ExperienceViewModel => ({
  id: '',
  name: '',
  chargeType: '',
  maxGuests: 0,
  minGuestsPerReservation: 0,
  slug: '',
  price: 0,
  referencePrice: undefined,
  referencePriceAnnotation: undefined,
  isPublicPrice: true,
  experienceDuration: undefined,
  cancellationPolicyTrigger: 'RESERVATION_CONFIRMED',
  isLadderEnabled: false,
  confirmedBookingLeadHours: 0,
  refundableCancellationDaysForGuest: 0,
  experienceOptions: [],
  interpreterOptions: [],
  reservationQuestions: [],
  priceIncludingTax: 0,
  images: [],
  tags: [],
  publishStatus: 'DRAFT',
  venueOverview: '',
  venueDetail: '',
  isInterpreterNeeded: false,
});

/**
 * APIレスポンスから体験画像ビューモデルを作成する
 *
 * @param {WabunkaExperienceImage[]} res - APIレスポンス
 * @returns {WabunkaExperienceImageListViewModel} - ビューモデル
 */
export const buildExperienceImageListViewModel = (res: WabunkaExperienceImage[]): ExperienceImageListViewModel => {
  return res
    .map((experienceImage) => ({
      id: experienceImage.id,
      experienceId: experienceImage.experienceId,
      imageUrl: experienceImage.imageUrl,
      sequenceId: experienceImage.sequenceId,
    }))
    .sort((a, b) => a.sequenceId - b.sequenceId);
};

/**
 * APIレスポンスから体験ビューモデルを作成する
 *
 * @param {WabunkaExperience} res - APIレスポンス
 * @returns {ExperienceViewModel} - ビューモデル
 */
export const buildExperienceViewModel = (res: WabunkaExperience | undefined): ExperienceViewModel => {
  if (!res) return createDefaultExperienceViewModel();
  return {
    id: res.id,
    name: res.name,
    maxGuests: res.maxGuests,
    minGuestsPerReservation: res.minGuestsPerReservation,
    slug: res.slug,
    chargeType: buildWabunkaChargeType(res.chargeType),
    provider: buildProviderViewModel(res.provider),
    price: res.price,
    referencePrice: res.referencePrice,
    referencePriceAnnotation: res.referencePriceAnnotation,
    isPublicPrice: res.isPublicPrice,
    experienceDuration: res.experienceDuration,
    cancellationPolicyTrigger: buildWabunkaCancellationPolicyTrigger(res.cancellationPolicyTrigger),
    isLadderEnabled: res.isLadderEnabled ?? false,
    confirmedBookingLeadHours: res.confirmedBookingLeadHours ?? 0,
    refundableCancellationDaysForGuest: res.refundableCancellationDaysForGuest,
    experienceOptions: buildWabunkaExperienceOptionListViewModel(res.experienceOptions),
    interpreterOptions: buildWabunkaInterpreterOptionListViewModel(res.experienceInterpreterOptions),
    reservationQuestions: buildWabunkaReservationQuestionListViewModel(res.experienceReservationQuestions),
    priceIncludingTax: calcPriceIncludingTax(res.price, DEFAULT_TAX_RATE),
    publishStatus: buildWabunkaPublishStatus(res.publishStatus),
    images: buildExperienceImageListViewModel(res.images),
    tags: buildWabunkaExperienceTagListViewModel(res.tags),
    venueOverview: res.venueOverview,
    venueDetail: res.venueDetail,
    currencyConvertedPrice: res.currencyConvertedPrice,
    isInterpreterNeeded: res.isInterpreterNeeded ?? false,
  };
};

/**
 * リスト表示用のビューモデル
 */
export type ExperienceListViewModel = ExperienceViewModel[];

/**
 * リスト表示用のビューモデルを組み立てる
 */
export const buildExperienceListViewModel = (experiences: WabunkaExperience[] | undefined): ExperienceListViewModel => {
  if (!experiences) {
    return [];
  }

  return experiences.map(buildExperienceViewModel);
};

const sortHistoryBySlugs = (unsortedExperiences: ExperienceListViewModel, historySlugs: string[]): ExperienceViewModel[] => {
  const historyMap = new Map<string, ExperienceViewModel>();
  for (const experience of unsortedExperiences) {
    historyMap.set(experience.slug, experience);
  }

  return historySlugs.map((slug) => historyMap.get(slug)).filter((history): history is ExperienceViewModel => history !== undefined);
};

/**
 * 閲覧履歴表示用のビューモデルを組み立てる
 */
export const buildExperienceBrowsingHistoryListViewModel = (experiences: WabunkaExperienceListResponse, historySlugs: string[]): ExperienceListViewModel => {
  if (experiences.items.length < 1) return [];

  const unsortedExperiences = experiences.items.map(buildExperienceViewModel);
  return sortHistoryBySlugs(unsortedExperiences, historySlugs);
};

/**
 * 宿泊用体験かどうかを返却する
 */
export const isOvernightStay = (experience: ExperienceViewModel) => {
  if (!experience.experienceDuration) return false;
  // 時間単位が「night,nights」の場合は宿泊用体験と判断する
  const isOvernightUnit = experience.experienceDuration.unit.startsWith('night');
  if (isOvernightUnit) return true;

  return false;
};

/**
 *日付・時間選択用のラベルを返却する
 */
export const buildDateTimePickerLabel = (isDateTimePicker: boolean, experience: ExperienceViewModel) => {
  // 時間選択時のラベル
  if (isDateTimePicker) {
    return isOvernightStay(experience) ? 'Select preferred check-in time (JST)' : 'Select preferred experience start time (JST)';
  }
  // 日付選択時のラベル
  return 'Select first preferred date (JST)';
};

/**
 * 体験料金を計算する(体験詳細でのデフォルト料金表示用)
 * 体験マスタベースで料金計算する際に必要
 */
// TODO:基本的にロジックはバックエンドに集約しているので、体験詳細での料金表示が不要になったら削除する。
export const calculateExperienceFee = (experiencePriceIncludingTax: number, numberOfParticipants: number, isGroup: boolean): number => {
  return isGroup ? experiencePriceIncludingTax : experiencePriceIncludingTax * numberOfParticipants;
};

/**
 * 体験料金・サービス手数料・合計税込金額を計算する(体験詳細でのデフォルト料金表示用)
 */
// TODO:基本的にロジックはバックエンドに集約しているので、体験詳細での料金表示が不要になったら削除する。
export const calculateDefaultExperiencePrices = (eventPriceIncludingTax: number, numberOfParticipants: number, isGroup: boolean, interpreterOptionPriceIncludingTax: number) => {
  const experienceFee = calculateExperienceFee(eventPriceIncludingTax, numberOfParticipants, isGroup);
  const interpreterFee = interpreterOptionPriceIncludingTax;
  const subtotal = Big(experienceFee).plus(interpreterFee).toNumber();
  const serviceFee = Big(subtotal).mul(SERVICE_FEE_RATE).round(0, 0).toNumber();

  return {
    experienceFee,
    serviceFee,
    discountAmount: 0,
    totalPriceIncludingTax: Big(subtotal).plus(serviceFee).toNumber(),
  };
};

/** 価格表示用のパーツ */
export interface PriceParts {
  /** 金額部分（例: "¥50,000"） */
  amount: string;
  /** 課金単位（例: " /person"） */
  chargeLabel: string;
}

/**
 * 参考価格を「金額」と「課金単位」に分割して返す
 * 個別にスタイル適用や改行制御ができるようにこの形で返す
 */
export const displayPriceParts = (experience: ExperienceViewModel): PriceParts | undefined => {
  const display = resolveReferencePriceDisplay(experience);
  const chargeLabel = ' /person';

  switch (display.kind) {
    case 'private':
      return { amount: 'Price upon request', chargeLabel: '' };
    case 'unpriced':
      return undefined;
    case 'converted':
      return { amount: `from ${formatCurrency(display.amount, display.currency)}`, chargeLabel };
    case 'jpy':
      return { amount: `from ¥${display.amount.toLocaleString()}`, chargeLabel };
  }
};

/**
 * 参考価格を表示用にフォーマットする
 * - referencePrice が未設定の場合は undefined を返す（価格欄を非表示にするため）
 * - 通貨換算価格がある場合はその価格を表示する
 * - 表示形式: "from ¥5,000 /person"
 * @param {ExperienceViewModel} experience
 * @returns string | undefined - 価格文字列、または未設定時は undefined
 */
export const displayPrice = (experience: ExperienceViewModel): string | undefined => {
  const parts = displayPriceParts(experience);
  if (!parts) {
    return undefined;
  }
  return `${parts.amount}${parts.chargeLabel}`;
};
